import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  input,
  model,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';

import { ButtonComponent } from '@shared/components/button/button.component';
import { ErrorsTooltipComponent } from '@shared/components/errors-tooltip/errors-tooltip.component';
import { InputComponent } from '@shared/components/input/input.component';
import { TooltipDirective } from '@shared/directives/tooltip.directive';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    InputComponent,
    FormsModule,
    ButtonComponent,
    TooltipDirective,
    NgClass,
    ErrorsTooltipComponent,
  ],
  selector: 'aux-note-textarea',
  standalone: true,
  styles: [
    `
      :host ::ng-deep textarea {
        box-shadow: none !important;
      }
    `,
  ],
  template: `
    <div
      class="flex h-full rounded-lg border border-aux-gray-dark p-4"
      [class.bg-aux-gray-dark-2]="disabled()"
    >
      <aux-input
        class="flex-grow"
        inputClassName="resize-none border-0 bg-transparent !outline-none !shadow-none w-full !max-w-full !text-lg text-aux-gray-darkest !font-normal"
        [textArea]="true"
        resize="none"
        [rows]="1"
        [(ngModel)]="notes"
        [placeholder]="placeholder()"
        [textareaAutoresize]="true"
        [disabled]="!editMode()"
      />

      <div class="flex w-[150px] min-w-[150px] items-start justify-end pt-0.5">
        @if (editMode()) {
          <aux-button
            class="mr-2"
            variant="primary"
            label="Save"
            data-pendo-id="invoice-notes-save-button"
            (click)="onSave()"
          />

          <aux-button variant="tertiary" label="Cancel" (click)="onCancel()" />
        } @else {
          @let reasonsToDisable = disabledTooltip();

          <aux-button
            variant="custom"
            icon="Pencil"
            svgAttributes="text-aux-blue"
            classList="py-0.5 mt-[5px]"
            [ngClass]="{
              'opacity-50': disabled(),
            }"
            type="button"
            [auxTooltip]="reasonsToDisable.length === 1 ? reasonsToDisable[0] : ''"
            [template]="reasonsToDisable.length > 1 ? tooltipTemplate : undefined"
            [disabled]="disabled()"
            (click)="onEdit()"
          />

          <ng-template #tooltipTemplate>
            <aux-errors-tooltip [errors]="reasonsToDisable" />
          </ng-template>
        }
      </div>
    </div>
  `,
})
export class NoteTextareaComponent {
  editMode = signal(false);

  notes = model.required<string>();

  disabled = input(false);
  disabledTooltip = input<string[]>([]);
  placeholder = input('Add a note...');

  cancelNotes = output<void>();
  saveNotes = output<void>();

  textarea = viewChild.required(InputComponent);

  initialValue = '';

  onCancel() {
    this.notes.set(this.initialValue);

    this.cancelNotes.emit();
    this.editMode.set(false);
  }

  onEdit() {
    this.initialValue = this.notes();

    this.editMode.set(true);
    const cmp = this.textarea();

    if (!cmp.input) {
      return;
    }

    setTimeout(() => {
      const end = cmp.input?.nativeElement.value.length || 0;

      cmp.input?.nativeElement.focus();
      cmp.input?.nativeElement.setSelectionRange(end, end);
    });
  }

  onSave() {
    this.saveNotes.emit();
    this.editMode.set(false);
  }
}
